import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Grid,
    Typography,
    TextField,
    InputAdornment,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    Menu,
    Checkbox,
    Popover,
    ListItemIcon,
    ListItemText,
    Divider
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import DeploymentModal from './components/DeploymentModal';
import { deleteFunction, batchUpdateFunctions } from 'store/slices/lambdaFunctions';

// icons
import {
    IconSearch,
    IconFilter,
    IconPlus,
    IconRefresh,
    IconDownload,
    IconTrash,
    IconEdit,
    IconChartLine,
    IconCopy,
    IconDots,
    IconCircleCheck,
    IconAlertCircle,
    IconClock,
    IconCode,
    IconRocket
} from '@tabler/icons-react';

// ==============================|| FUNCTIONS LIST ||============================== //

const FunctionsList = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { functions, loading } = useSelector((state) => state.lambdaFunctions);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [selectedFunctions, setSelectedFunctions] = useState([]);
    const [deployModalOpen, setDeployModalOpen] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedFunction, setSelectedFunction] = useState(null);

    // Filter states
    const [filters, setFilters] = useState({
        status: 'all',
        version: 'all',
        dateRange: 'all'
    });

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedFunctions(filteredFunctions.map((f) => f.id));
        } else {
            setSelectedFunctions([]);
        }
    };

    const handleSelectFunction = (functionId) => {
        setSelectedFunctions((prev) => {
            if (prev.includes(functionId)) {
                return prev.filter((id) => id !== functionId);
            }
            return [...prev, functionId];
        });
    };

    const handleBulkDelete = () => {
        if (window.confirm(`Are you sure you want to delete ${selectedFunctions.length} functions?`)) {
            selectedFunctions.forEach((id) => {
                dispatch(deleteFunction(id));
            });
            setSelectedFunctions([]);
        }
    };

    const handleBulkDeploy = () => {
        const functionsToUpdate = selectedFunctions.map((id) => ({
            id,
            status: 'deploying'
        }));
        dispatch(batchUpdateFunctions(functionsToUpdate));

        // Simulate deployment
        setTimeout(() => {
            const updates = selectedFunctions.map((id) => ({
                id,
                status: 'deployed',
                updated_at: new Date().toISOString()
            }));
            dispatch(batchUpdateFunctions(updates));
            setSelectedFunctions([]);
        }, 2000);
    };

    const handleExport = () => {
        const dataToExport = selectedFunctions.length > 0 ? functions.filter((f) => selectedFunctions.includes(f.id)) : filteredFunctions;

        const csv = [
            ['Name', 'Status', 'Version', 'Endpoint', 'Executions (24h)', 'Success Rate', 'Created At'],
            ...dataToExport.map((f) => [
                f.name,
                f.status,
                f.version,
                f.endpoint,
                f.metrics.total_executions_24h,
                f.metrics.success_rate,
                f.created_at
            ])
        ]
            .map((row) => row.join(','))
            .join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `lambda_functions_${new Date().toISOString()}.csv`;
        a.click();
    };

    const handleMenuOpen = (event, func) => {
        setMenuAnchorEl(event.currentTarget);
        setSelectedFunction(func);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedFunction(null);
    };

    const handleFunctionAction = (action) => {
        switch (action) {
            case 'edit':
                navigate(`/lambda/functions/${selectedFunction.id}/edit`);
                break;
            case 'test':
                navigate(`/lambda/test/${selectedFunction.id}`);
                break;
            case 'metrics':
                navigate(`/lambda/metrics/${selectedFunction.id}`);
                break;
            case 'clone':
                // Clone function logic
                break;
            case 'delete':
                if (window.confirm(`Are you sure you want to delete ${selectedFunction.name}?`)) {
                    dispatch(deleteFunction(selectedFunction.id));
                }
                break;
        }
        handleMenuClose();
    };

    // Filter functions
    const filteredFunctions = functions.filter((func) => {
        const matchesSearch =
            func.name.toLowerCase().includes(searchTerm.toLowerCase()) || func.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filters.status === 'all' || func.status === filters.status;
        return matchesSearch && matchesStatus;
    });

    const paginatedFunctions = filteredFunctions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const getStatusColor = (status) => {
        switch (status) {
            case 'deployed':
                return 'success';
            case 'deploying':
                return 'warning';
            case 'failed':
                return 'error';
            default:
                return 'default';
        }
    };

    return (
        <>
            <MainCard
                title={
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h3">Lambda Functions</Typography>
                        <Button variant="contained" startIcon={<IconRocket />} onClick={() => setDeployModalOpen(true)}>
                            Deploy New Function
                        </Button>
                    </Box>
                }
            >
                {/* Toolbar */}
                <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                placeholder="Search functions..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconSearch size={18} />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <Box display="flex" gap={1} justifyContent="flex-end">
                                {selectedFunctions.length > 0 && (
                                    <>
                                        <Button variant="outlined" color="error" startIcon={<IconTrash />} onClick={handleBulkDelete}>
                                            Delete ({selectedFunctions.length})
                                        </Button>
                                        <Button variant="outlined" startIcon={<IconRocket />} onClick={handleBulkDeploy}>
                                            Deploy ({selectedFunctions.length})
                                        </Button>
                                    </>
                                )}

                                <Tooltip title="Filter">
                                    <IconButton onClick={(e) => setFilterAnchorEl(e.currentTarget)}>
                                        <IconFilter />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Export">
                                    <IconButton onClick={handleExport}>
                                        <IconDownload />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Refresh">
                                    <IconButton>
                                        <IconRefresh />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Table */}
                <TableContainer component={Paper} variant="outlined">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={selectedFunctions.length > 0 && selectedFunctions.length < filteredFunctions.length}
                                        checked={filteredFunctions.length > 0 && selectedFunctions.length === filteredFunctions.length}
                                        onChange={handleSelectAll}
                                    />
                                </TableCell>
                                <TableCell>Function Name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Version</TableCell>
                                <TableCell>Runtime</TableCell>
                                <TableCell>Last Modified</TableCell>
                                <TableCell>Executions (24h)</TableCell>
                                <TableCell>Success Rate</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedFunctions.map((func) => (
                                <TableRow key={func.id} hover selected={selectedFunctions.includes(func.id)}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedFunctions.includes(func.id)}
                                            onChange={() => handleSelectFunction(func.id)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="subtitle2">{func.name}</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {func.description}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={func.status}
                                            size="small"
                                            color={getStatusColor(func.status)}
                                            icon={
                                                func.status === 'deployed' ? (
                                                    <IconCircleCheck size={14} />
                                                ) : func.status === 'failed' ? (
                                                    <IconAlertCircle size={14} />
                                                ) : (
                                                    <IconClock size={14} />
                                                )
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={`v${func.version}`} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <IconCode size={16} />
                                            <Typography variant="caption">Python 3.9</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">{new Date(func.updated_at).toLocaleString()}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{func.metrics.total_executions_24h.toLocaleString()}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Typography
                                                variant="body2"
                                                color={func.metrics.success_rate > 95 ? 'success.main' : 'warning.main'}
                                            >
                                                {func.metrics.success_rate}%
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                                            <Tooltip title="Test">
                                                <IconButton size="small" onClick={() => navigate(`/lambda/test/${func.id}`)}>
                                                    <IconRocket size={18} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Metrics">
                                                <IconButton size="small" onClick={() => navigate(`/lambda/metrics/${func.id}`)}>
                                                    <IconChartLine size={18} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="More">
                                                <IconButton size="small" onClick={(e) => handleMenuOpen(e, func)}>
                                                    <IconDots size={18} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredFunctions.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />

                {filteredFunctions.length === 0 && (
                    <Box textAlign="center" py={8}>
                        <Typography variant="h5" color="textSecondary" gutterBottom>
                            No functions found
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                            Deploy your first Lambda function to get started
                        </Typography>
                        <Button variant="contained" startIcon={<IconPlus />} onClick={() => setDeployModalOpen(true)}>
                            Deploy Function
                        </Button>
                    </Box>
                )}
            </MainCard>

            {/* Filter Popover */}
            <Popover
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
            >
                <Box sx={{ p: 2, minWidth: 250 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Filters
                    </Typography>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={filters.status}
                            label="Status"
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            size="small"
                        >
                            <MenuItem value="all">All Status</MenuItem>
                            <MenuItem value="deployed">Deployed</MenuItem>
                            <MenuItem value="deploying">Deploying</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                        </Select>
                    </FormControl>

                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                            setFilters({ status: 'all', version: 'all', dateRange: 'all' });
                            setFilterAnchorEl(null);
                        }}
                    >
                        Clear Filters
                    </Button>
                </Box>
            </Popover>

            {/* Action Menu */}
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={() => handleFunctionAction('edit')}>
                    <ListItemIcon>
                        <IconEdit size={18} />
                    </ListItemIcon>
                    <ListItemText>Edit Function</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleFunctionAction('test')}>
                    <ListItemIcon>
                        <IconRocket size={18} />
                    </ListItemIcon>
                    <ListItemText>Test Function</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleFunctionAction('metrics')}>
                    <ListItemIcon>
                        <IconChartLine size={18} />
                    </ListItemIcon>
                    <ListItemText>View Metrics</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleFunctionAction('clone')}>
                    <ListItemIcon>
                        <IconCopy size={18} />
                    </ListItemIcon>
                    <ListItemText>Clone Function</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => handleFunctionAction('delete')}>
                    <ListItemIcon>
                        <IconTrash size={18} color={theme.palette.error.main} />
                    </ListItemIcon>
                    <ListItemText sx={{ color: 'error.main' }}>Delete Function</ListItemText>
                </MenuItem>
            </Menu>

            {/* Deployment Modal */}
            <DeploymentModal open={deployModalOpen} onClose={() => setDeployModalOpen(false)} />
        </>
    );
};

export default FunctionsList;
